<template>
	<div id="lp-index" :class="['lp-index', modeClass]">
		<slot name="toolbar" />

		<panel-wrapper panelSide="left" :leftPanelSelected="leftPanelSelected">
			<left-panel
				:leftPanelsTop="leftPanelsTop"
				:leftPanelsBottom="leftPanelsBottom"
				:leftPanelSelected.sync="leftPanelSelected"
				@slideChanged="$emit('slideChanged', blocksSelector)"
			/>
		</panel-wrapper>

		<main
			class="lp-index-main"
			:style="{
				marginLeft: (leftPanelSelected > -1 ? 400 : 0) + 'px',
				marginRight: (rightPanelSelected > -1 ? lessonPlanController.rightPanelMargin : 0) + 'px'
			}"
		>
			<footer-arrows
				@slideChanged="$emit('slideChanged', blocksSelector)"
				@prev="goToPrevSlide"
				@next="goToNextSlide"
				@update="goToSlideIndex"
				@toggleFullscreen="toggleFullscreen"
				@toggleSubtitles="toggleSubtitles"
				@selectDefaultLang="selectDefaultLang"
				@startDuplicateScreen="startDuplicateScreen"
				@startScreenSharing="startScreenSharing"
				:slide="lessonPlanController.navigation.selectedSlideIndex + 1"
				:total="lessonPlanController.navigation.slidesVisible.length"
				:can-go-next="lessonPlanController.navigation.canGoNext"
				:can-go-prev="lessonPlanController.navigation.canGoPrev"
				:isFullscreen="lessonPlanController.isFullscreen"
				:subtitlesActive="lessonPlanController.subtitlesActive"
				:defaultSubtitles="lessonPlanController.defaultSubtitles"
				:can-start-presenter-mode="canStartPresenterMode"
				:can-show-drawing-tools-button="canShowDrawingToolsButton"
			/>

			<block-canvas-readonly
				ref="canvas"
				:blocks="blocks"
				:key="slideId"
				:defaultBlockStyles="defaultBlockStyles"
				:settingStyles="settingStyles"
				:globalVariables="globalVariables"
				:class="canvasClass"
				:mode="mode"
				@selectSlideById="selectSlideById"
				@goToBookmarkTarget="goToBookmarkTarget"
				@openObjectives="openObjectivesPanel"
				@submitQuiz="onSubmitQuiz"
			/>

			<div v-show="showDrawingCanvas" class="drawing-canvas-wrapper" :style="drawingCanvasStyle">
				<div class="canvas-cursor"></div>
				<canvas ref="drawingCanvas" width="1920" height="1080" />
			</div>
		</main>

		<panel-wrapper panelSide="right" :rightPanelSelected="rightPanelSelected">
			<right-panel
				:rightPanels="rightPanels"
				:rightPanelSelected.sync="rightPanelSelected"
				:key="slideId"
				:readonly="true"
				@update:rightPanelSelected="handleUpdateRightPanelSelected"
			/>
		</panel-wrapper>
	</div>
</template>

<script>
import Vue from 'vue';
import LeftPanel from '../../../components/structure/LeftPanel.vue';
import RightPanel from '../../../components/structure/RightPanel.vue';
import PanelWrapper from '../../../components/structure/PanelWrapper.vue';
import BlockCanvasReadonly from '../containers/BlockCanvasReadonly.vue';
import FooterArrows from '../../../../commonInteractive/navigation/FooterArrows.vue';
import presenterModeMixin from '../../../mixins/PresenterMode';
import { createBlocksSelector } from '../../../controllers/BlocksSelector/blocksSelectorFactory.js';
import { createSlideStyleData } from '../../../controllers/SlideStyleData/slideStyleDataFactory';
import { createDrawingTools } from '../../../controllers/DrawingTools/drawingToolsFactory.js';

export default {
	name: 'WrapperReadonlyModes',
	inject: ['lessonPlanController'],
	mixins: [presenterModeMixin],
	components: {
		BlockCanvasReadonly,
		FooterArrows,
		LeftPanel,
		PanelWrapper,
		RightPanel
	},
	provide() {
		return {
			blockDefinitions: this.blockDefinitions,
			blocksSelector: this.blocksSelector,
			slideStyleData: this.slideStyleData,
			drawingTools: this.drawingTools,
			blockEventBus: new Vue()
		};
	},
	props: {
		blockDefinitions: Object,
		blocks: Array,
		canStartPresenterMode: Boolean,
		canvasClass: String,
		defaultBlockStyles: Object,
		globalVariables: Array,
		leftPanelsBottom: Array,
		leftPanelsTop: Array,
		mode: String,
		modeClass: String,
		rightPanels: Array,
		settingStyles: Object,
		slideId: String
	},
	data() {
		return {
			leftPanelSelected: -1,
			rightPanelSelected: -1,
			blocksSelector: createBlocksSelector(this),
			slideStyleData: createSlideStyleData(this),
			drawingTools: createDrawingTools()
		};
	},
	mounted() {
		document.addEventListener('keydown', this.navigationKeyHandler);

		if (this.isDuplicateScreenAndStudent) this.initDrawingTools();
	},
	unmounted() {
		document.removeEventListener('keydown', this.navigationKeyHandler);

		if (this.isDuplicateScreenAndStudent) this.destroyDrawingTools();
	},
	computed: {
		selectedSlide() {
			return this.lessonPlanController.navigation.selectedSlide;
		},
		isSpeakerNotesPanel() {
			const selectedPanel = this.rightPanels[this.rightPanelSelected];
			return selectedPanel && selectedPanel.title === 'Speaker Notes';
		},
		isAudienceNotesPanel() {
			const selectedPanel = this.rightPanels[this.rightPanelSelected];
			return selectedPanel && selectedPanel.title === 'Audience Notes';
		},
		isSpeakerNotesWindowActive() {
			return this.lessonPlanController.isSpeakerNotesWindowActive;
		},
		isAudienceNotesWindowActive() {
			return this.lessonPlanController.isAudienceNotesWindowActive;
		},
		cannotOpenSpeakerNotesPanel() {
			return this.isSpeakerNotesWindowActive || this.isScreenSharingActive || this.isDuplicateScreenAndStudent;
		},
		cannotOpenAudienceNotesPanel() {
			return this.isAudienceNotesWindowActive;
		},
		isDuplicateScreenAndStudent() {
			return this.isDuplicateScreenActive && this.isStudentWindowActive;
		},
		showDrawingCanvas() {
			return this.isDuplicateScreenAndStudent || this.isScreenSharingActive;
		},
		canShowDrawingToolsButton() {
			return this.isDuplicateScreenAndStudent || this.isScreenSharingActive;
		},
		drawingCanvasStyle() {
			return { 'pointer-events': this.drawingTools.isDrawingToolActive ? 'initial' : 'none' };
		}
	},
	methods: {
		startDuplicateScreen() {
			this.lessonPlanController.startDuplicateScreen(this.$route);
		},
		startScreenSharing() {
			this.lessonPlanController.startScreenSharing();
		},
		openObjectivesPanel() {
			const objIndex = this.leftPanelsTop.findIndex(panel => panel.title === 'Objectives Level');
			this.leftPanelSelected = objIndex;
		},
		selectSlideById(id) {
			this.$emit('selectSlideById', id);
			this.blocksSelector.closeAllOverlays();
		},
		goToBookmarkTarget(bookmark) {
			this.$emit('goToBookmarkTarget', bookmark);
			this.blocksSelector.closeAllOverlays();
		},
		onSubmitQuiz(quiz) {
			this.$emit('submitQuiz', quiz);
		},
		navigationKeyHandler(e) {
			// Avoid changing slide when editing comments
			if (e.srcElement !== document.body) return;

			switch (e.keyCode) {
				case 33: // page up
				case 37: // left
				case 38: // up
					// go to previous
					this.goToPrevSlide();
					break;
				case 34: // page down
				case 39: // right
				case 40: // down
					// go to next
					this.goToNextSlide();
					break;
				case 27: // esc
					this.lessonPlanController.setFullscreenTo(false);
					break;
				default:
					return; // exit this handler for other keys
			}
		},
		goToPrevSlide() {
			this.blocksSelector.closeAllOverlays();
			this.lessonPlanController.navigation.prevSlide();
		},
		goToNextSlide() {
			this.blocksSelector.closeAllOverlays();
			this.lessonPlanController.navigation.nextSlide();
		},
		goToSlideIndex(number) {
			const index = number - 1;
			if (index < this.lessonPlanController.navigation.slidesVisible.length && index > -1) {
				const slideVisible = this.lessonPlanController.navigation.slidesVisible[index];
				const slideNav = this.lessonPlanController.navigation.slideNavigationMap.find(
					slideN => slideN.id === slideVisible.id
				);
				if (slideNav && !slideNav.available) {
					// avoid to select slide
					return;
				}
				this.blocksSelector.closeAllOverlays();
				this.lessonPlanController.navigation.selectSlideByIndex(index);
			}
		},
		toggleFullscreen() {
			this.lessonPlanController.toggleFullscreen();
		},
		toggleSubtitles() {
			this.lessonPlanController.toggleSubtitles();
		},
		selectDefaultLang(lang) {
			this.lessonPlanController.selectDefaultLang(lang);
		},
		handleSpeakerNotesSelection(previousSelectedIndex) {
			this.lessonPlanController.checkIsSpeakerNotesWindowActive();

			if (this.isScreenSharingActive) {
				this.lessonPlanController.openSpeakerNotesWindow();
			}

			if (this.cannotOpenSpeakerNotesPanel) {
				this.rightPanelSelected = previousSelectedIndex;
			}
		},
		handleAudienceNotesSelection(previousSelectedIndex) {
			this.lessonPlanController.checkIsAudienceNotesWindowActive();

			if (this.cannotOpenAudienceNotesPanel) {
				this.rightPanelSelected = previousSelectedIndex;
			}
		},
		handleUpdateRightPanelSelected(index) {
			if (index === -1) this.lessonPlanController.lastRightPanelSelectedTitle = null;
		},
		validateRightPanelSelected() {
			const selectedPanelIndex = this.rightPanels.findIndex(
				panel => panel.title === this.lessonPlanController.lastRightPanelSelectedTitle
			);

			this.rightPanelSelected = selectedPanelIndex;
		},
		updateLastRightPanelTitle(index) {
			if (index === -1) return;

			const panel = this.rightPanels[index];

			this.lessonPlanController.lastRightPanelSelectedTitle = panel.title;
		},
		initDrawingTools() {
			this.drawingTools.initDrawingTools(
				this.$refs.drawingCanvas,
				'.drawing-canvas-wrapper',
				'.lp-slide',
				'.canvas-cursor'
			);
		},
		destroyDrawingTools() {
			this.drawingTools.destroyDrawingTools();
		}
	},
	watch: {
		rightPanelSelected: {
			handler(newSelectedIndex, previousSelectedIndex) {
				if (newSelectedIndex === -1) return;

				if (this.isSpeakerNotesPanel) {
					this.handleSpeakerNotesSelection(previousSelectedIndex);
				} else if (this.isAudienceNotesPanel) {
					this.handleAudienceNotesSelection(previousSelectedIndex);
				}

				this.updateLastRightPanelTitle(this.rightPanelSelected);
			}
		},
		selectedSlide: {
			handler() {
				this.validateRightPanelSelected();
				this.drawingTools.clearCanvas();
			}
		},
		isScreenSharingActive: {
			handler() {
				if (this.isScreenSharingActive) {
					this.initDrawingTools();
				} else {
					this.destroyDrawingTools();
				}

				const isRightPanelSelected = this.rightPanelSelected !== -1;
				const shouldCloseRightPanel = this.isSpeakerNotesPanel && this.isScreenSharingActive && isRightPanelSelected;

				if (shouldCloseRightPanel) this.rightPanelSelected = -1;
			}
		},
		isSpeakerNotesWindowActive: {
			handler() {
				if (this.isSpeakerNotesPanel && this.isSpeakerNotesWindowActive) this.rightPanelSelected = -1;
			}
		},
		isAudienceNotesWindowActive: {
			handler() {
				if (this.isAudienceNotesPanel && this.isAudienceNotesWindowActive) this.rightPanelSelected = -1;
			}
		}
	}
};
</script>

<style>
.drawing-canvas-wrapper {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 32px;
	z-index: 100;
}

.drawing-canvas-wrapper .canvas-cursor {
	display: none;
	border: 2px solid black;
	border-radius: 50%;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 100;
	pointer-events: none;
	transform: translate(-50%, -50%);
	transition: transform 0.05s linear;
}

.drawing-canvas-wrapper canvas {
	position: absolute;
	left: 0;
	top: 0;
}
</style>
